/**
 * Client-side BoM rule preview, aligned with the PLV8 logic described in docs/bom-system-complete.md
 * (condition regex on parsed features, replacement with {name} and {existing} from the working BOM).
 */

export type BomRuleRow = Record<string, unknown>;

export type RuleRowPreviewResult =
  | { kind: "ok"; conditionMet: boolean; expandedReplacement: string; featureSample?: string }
  | { kind: "no_parse" }
  | { kind: "no_test_sku" }
  | { kind: "no_initial" }
  | { kind: "invalid_regex"; message: string }
  | { kind: "no_target_variable"; variableName: string };

type ParsedSku = Record<string, string | string[] | undefined>;

type BomState = Map<string, { component_sku: string | null; quantity: number }>;

function mergeWithEditing(
  row: BomRuleRow,
  editing: { id: number; draft: Record<string, unknown> } | null,
): BomRuleRow {
  if (editing && Number(row.id) === Number(editing.id)) {
    return { ...row, ...editing.draft };
  }
  return row;
}

/**
 * API returns rows; the first object is a flat feature map.
 */
export function parseRowsToParsedSku(rows: unknown[] | null | undefined): ParsedSku | null {
  if (rows == null || rows.length === 0) {
    return null;
  }
  const first = rows[0];
  if (first == null || typeof first !== "object" || Array.isArray(first)) {
    return null;
  }
  const out: ParsedSku = {};
  for (const [key, v] of Object.entries(first as Record<string, unknown>)) {
    if (v == null) {
      out[key] = undefined;
    } else if (Array.isArray(v)) {
      out[key] = v.map((x) => String(x));
    } else {
      out[key] = String(v);
    }
  }
  return out;
}

function buildInitialBomState(initialRows: Record<string, unknown>[]): BomState {
  const m: BomState = new Map();
  for (const row of initialRows) {
    const vn = String(row.variable_name ?? "");
    if (vn.length === 0) {
      continue;
    }
    m.set(vn, {
      component_sku: row.component_sku == null || row.component_sku === "" ? null : String(row.component_sku),
      quantity: Number(row.quantity ?? 0),
    });
  }
  return m;
}

function expandReplacementPattern(
  pattern: string,
  target: { component_sku: string | null; quantity: number },
  parsed: ParsedSku,
): string {
  return pattern.replace(/\{(.*?)\}/g, (_m, name: string) => {
    if (name === "existing") {
      if (target.component_sku == null || target.component_sku === "") {
        return "";
      }
      return String(target.component_sku);
    }
    const v = parsed[name];
    if (v === undefined) {
      return "";
    }
    if (Array.isArray(v)) {
      return v.map(String).join("");
    }
    return String(v);
  });
}

function testCondition(
  merged: BomRuleRow,
  parsed: ParsedSku,
):
  | { success: true; met: boolean; featureSample: string }
  | { success: false; error: string } {
  const cf = merged.condition_feature;
  const cs = merged.condition_search;
  const featureName = cf == null ? "" : String(cf).trim();
  const searchStr = cs == null ? "" : String(cs).trim();
  if (featureName.length === 0 || searchStr.length === 0) {
    return { success: true, met: true, featureSample: "" };
  }
  let re: RegExp;
  try {
    re = new RegExp(searchStr);
  } catch (e) {
    const message = e instanceof Error ? e.message : "Invalid regular expression";
    return { success: false, error: message };
  }
  const raw = parsed[featureName];
  if (raw === undefined) {
    return { success: true, met: false, featureSample: "" };
  }
  if (Array.isArray(raw)) {
    const met = raw.some((el) => re.test(String(el)));
    const sample = raw.map(String).join(", ");
    const featureSample = sample.length > 120 ? `${sample.slice(0, 120)}…` : sample;
    return { success: true, met, featureSample };
  }
  const s = String(raw);
  const featureSample = s.length > 80 ? `${s.slice(0, 80)}…` : s;
  return { success: true, met: re.test(s), featureSample };
}

/**
 * Returns whether the condition matches and whether the regex was valid.
 * If invalid, `met` is false and `error` is set.
 */
function applyOneRule(bom: BomState, merged: BomRuleRow, parsed: ParsedSku): void {
  const variableName = String(merged.variable_name ?? "");
  const target = bom.get(variableName);
  if (!target) {
    return;
  }
  const cond = testCondition(merged, parsed);
  if (!cond.success) {
    return;
  }
  if (!cond.met) {
    return;
  }
  const pattern = String(merged.replacement_sku_pattern ?? "");
  const replacement = expandReplacementPattern(pattern, target, parsed);
  target.component_sku = replacement;
  const mult = Number(merged.multiplier ?? 1);
  if (!Number.isNaN(mult)) {
    target.quantity = Number(target.quantity) * mult;
  }
}

export function sortTemplateRules(rules: BomRuleRow[]): BomRuleRow[] {
  return [...rules].sort((a, b) => {
    const pa = Number(a.priority ?? 0);
    const pb = Number(b.priority ?? 0);
    if (pa !== pb) {
      return pa - pb;
    }
    return Number(a.id) - Number(b.id);
  });
}

/**
 * Per-row preview: apply all rules before this one (with merged draft for the row being edited), then
 * evaluate this rule (merged) for condition and expanded replacement.
 */
export function computeRuleRowPreview(
  targetRuleId: number,
  input: {
    testSku: string;
    initialRows: Record<string, unknown>[];
    rules: BomRuleRow[];
    parseRows: unknown[] | null | undefined;
    editing: { id: number; draft: Record<string, unknown> } | null;
  },
): RuleRowPreviewResult {
  if (!input.testSku.trim()) {
    return { kind: "no_test_sku" };
  }
  const parsed = parseRowsToParsedSku(input.parseRows);
  if (parsed == null) {
    return { kind: "no_parse" };
  }
  if (input.initialRows.length === 0) {
    return { kind: "no_initial" };
  }
  const sorted = sortTemplateRules(input.rules);
  const index = sorted.findIndex((r) => Number(r.id) === targetRuleId);
  if (index < 0) {
    return { kind: "no_target_variable", variableName: "—" };
  }
  const bom = buildInitialBomState(input.initialRows);
  for (let i = 0; i < index; i += 1) {
    const rule = sorted[i];
    const merged = mergeWithEditing(rule, input.editing);
    applyOneRule(bom, merged, parsed);
  }
  const current = sorted[index];
  const merged = mergeWithEditing(current, input.editing);
  const variableName = String(merged.variable_name ?? "");
  const target = bom.get(variableName);
  if (!target) {
    return { kind: "no_target_variable", variableName: variableName.length > 0 ? variableName : "—" };
  }
  const cond = testCondition(merged, parsed);
  if (!cond.success) {
    return { kind: "invalid_regex", message: cond.error };
  }
  if (!cond.met) {
    return {
      kind: "ok",
      conditionMet: false,
      expandedReplacement: "",
      featureSample: cond.featureSample,
    };
  }
  const pattern = String(merged.replacement_sku_pattern ?? "");
  const expandedReplacement = expandReplacementPattern(pattern, target, parsed);
  return {
    kind: "ok",
    conditionMet: true,
    expandedReplacement,
    featureSample: cond.featureSample,
  };
}
